import { Fragment, h, render } from "preact";
import { useEffect, useState, useMemo, useCallback } from "preact/hooks";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";

const ZOOMS = [0, 1, 1.5];

const ZoomableImageTransform = ({ alt, src, scale, initialScale }) => {
  const { resetTransform, centerView } = useControls();

  useEffect(() => {
    if (scale === 0) {
      resetTransform(500);
    } else {
      centerView(scale, 500);
    }
  }, [scale]);

  return (
    <TransformComponent
      wrapperStyle={{
        width: "100%",
        height: "100%",
      }}
    >
      <img alt={alt} src={src} />
    </TransformComponent>
  );
};

export default function ZoomableImage({ src, alt }) {
  const scaleUp = true;

  const [container, setContainer] = useState(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const [imageNaturalWidth, setImageNaturalWidth] = useState(0);
  const [imageNaturalHeight, setImageNaturalHeight] = useState(0);

  const imageScale = useMemo(() => {
    if (
      containerWidth === 0 ||
      containerHeight === 0 ||
      imageNaturalWidth === 0 ||
      imageNaturalHeight === 0
    )
      return 0;
    const scale = Math.min(
      containerWidth / imageNaturalWidth,
      containerHeight / imageNaturalHeight
    );
    return scaleUp ? scale : Math.max(scale, 1);
  }, [
    scaleUp,
    containerWidth,
    containerHeight,
    imageNaturalWidth,
    imageNaturalHeight,
  ]);

  const initialPositions = useMemo(() => {
    const positions = { x: 0, y: 0 };
    if (
      imageScale === 0 ||
      containerWidth === 0 ||
      containerHeight === 0 ||
      imageNaturalWidth === 0 ||
      imageNaturalHeight === 0
    )
      return positions;

    const containerAspectRatio = containerWidth / containerHeight;
    const imageAspectRatio = imageNaturalWidth / imageNaturalHeight;

    const imageWidth = imageNaturalWidth * imageScale;
    const imageHeight = imageNaturalHeight * imageScale;

    if (containerAspectRatio > imageAspectRatio) {
      // fill height
      positions.x = 0.5 * (containerWidth - imageWidth);
    } else {
      positions.y = 0.5 * (containerHeight - imageHeight);
    }
    return positions;
  }, [containerWidth, containerHeight, imageNaturalWidth, imageNaturalHeight]);

  const handleResize = useCallback(() => {
    if (container !== null) {
      const rect = container.getBoundingClientRect();
      setContainerWidth(rect.width);
      setContainerHeight(rect.height);
    } else {
      setContainerWidth(0);
      setContainerHeight(0);
    }
  }, [container]);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const handleImageOnLoad = (image) => {
    setImageNaturalWidth(image.naturalWidth);
    setImageNaturalHeight(image.naturalHeight);
  };

  useEffect(() => {
    const image = new Image();
    image.onload = () => handleImageOnLoad(image);
    image.src = src;
  }, [src]);

  const [scale, setScale] = useState(0);
  const [clickCoords, setClickCoords] = useState(null);
  const [zoomIndex, setZoomIndex] = useState(0);

  const handleMouseDown = (e) => {
    setClickCoords({ x: e.clientX, y: e.clientY });
  };

  const handleClick = (e) => {
    // Mouse has moved - image will pan instead of zooming
    if (e.clientX !== clickCoords.x || e.clientY !== clickCoords.y) return;

    const nextZoomIndex = zoomIndex === ZOOMS.length - 1 ? 0 : zoomIndex + 1;
    setScale(ZOOMS[nextZoomIndex]);
    setZoomIndex(nextZoomIndex);
  };

  const cursor =
    scale === ZOOMS[ZOOMS.length - 1]
      ? `url(data:image/svg+xml,%3Csvg%20width%3D%2246%22%20height%3D%2246%22%20viewBox%3D%220%200%2046%2046%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Crect%20width%3D%2246%22%20height%3D%2246%22%20rx%3D%2223%22%20fill%3D%22white%22%20fill-opacity%3D%220.7%22%2F%3E%0A%3Crect%20x%3D%228%22%20y%3D%2221%22%20width%3D%2231%22%20height%3D%224%22%20fill%3D%22black%22%2F%3E%0A%3C%2Fsvg%3E), zoom-out`
      : `url(data:image/svg+xml,%3Csvg%20width%3D%2246%22%20height%3D%2246%22%20viewBox%3D%220%200%2046%2046%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Crect%20width%3D%2246%22%20height%3D%2246%22%20rx%3D%2223%22%20fill%3D%22white%22%20fill-opacity%3D%220.7%22%2F%3E%0A%3Cpath%20d%3D%22M21.0832%2029.7083L24.9598%2029.7087V38.3344L21.0832%2038.3341V29.7083Z%22%20fill%3D%22black%22%2F%3E%0A%3Cpath%20d%3D%22M21.0832%207.66699H24.9165V21.0834L38.3332%2021.0837V24.8538H7.6665V21.0837H21.0832V7.66699Z%22%20fill%3D%22black%22%2F%3E%0A%3C%2Fsvg%3E), zoom-in`;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        cursor,
      }}
      ref={(el) => setContainer(el)}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      {imageScale > 0 && (
        <TransformWrapper
          key={`${containerWidth}x${containerHeight}`}
          initialScale={imageScale}
          minScale={imageScale}
          maxScale={ZOOMS[ZOOMS.length - 1]}
          centerOnInit={false}
          initialPositionX={initialPositions.x}
          initialPositionY={initialPositions.y}
          limitToBounds={true}
          wheel={{ disabled: true }}
          doubleClick={{ disabled: true }}
        >
          <ZoomableImageTransform
            alt={alt}
            src={src}
            scale={scale}
            initialScale={imageScale}
          />
        </TransformWrapper>
      )}
    </div>
  );
}
